import { redirect, type ParsedLocation } from "@tanstack/react-router";

import { sdk } from "@/api/sdk";
import { LegalConsentKind, LegalConsentState, legalConsentsToAsk, type LegalConsentStatus } from "@complete-athlete/enums";

/** The consents this app asks for: signup ticks both here, and the health tick stays its own. */
export const APP_LEGAL_CONSENTS = [LegalConsentKind.TERMS, LegalConsentKind.HEALTH_DATA];

/** The pages that are about health data, closed while the consent is withdrawn. */
const HEALTH_PATHS = ["/training", "/diet", "/progress"];

const needsHealthData = (pathname: string) =>
  HEALTH_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

/** The status of the signed-in person, so a navigation does not ask the API every time. */
let known: { userId: string; status: LegalConsentStatus } | null = null;

/** Called when the person changes a consent, so the gate follows without a reload. */
export const rememberLegalConsentStatus = (userId: string, status: LegalConsentStatus) => {
  known = { userId, status };
};

export const legalConsentStatusOf = async (userId: string): Promise<LegalConsentStatus | null> => {
  if (known?.userId === userId) return known.status;
  const { data } = await sdk.legalConsents.$get.send();
  if (data) rememberLegalConsentStatus(userId, data);
  return data;
};

/**
 * Sends anyone who has not agreed to the documents in force to the consent screen, and anyone who
 * withdrew health data consent away from the pages that need it. An API that does not answer lets
 * them through: the server still refuses health data without a consent on record.
 */
export const guardLegalConsents = async ({ userId, location }: { userId: string; location: ParsedLocation }) => {
  const status = await legalConsentStatusOf(userId);
  if (!status) return;
  const search = location.pathname === "/" ? {} : { redirect: location.href };
  if (legalConsentsToAsk(status, APP_LEGAL_CONSENTS).length > 0) {
    throw redirect({ to: "/consent", search });
  }
  if (status[LegalConsentKind.HEALTH_DATA] === LegalConsentState.WITHDRAWN && needsHealthData(location.pathname)) {
    throw redirect({ to: "/consent", search: { ...search, health: true } });
  }
};
